import React, { Component } from "react";
import {
  Table,
  Form,
  FormInstance,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Popconfirm,
  Typography,
  Space,
  Button,
  Divider,
} from "antd";
import {
  FormOutlined,
  MenuOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  StopOutlined,
  SaveOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import DataUtil, { myFlat } from "../utils/DataUtil";
import { arrayMoveImmutable } from "array-move";

interface IProps {
  columns: any[];
  datas: any[];
  record_key: string;
  saveCallback: (record: any) => void;
  deleteCallback: (record_key: string) => void;
  reloadCallback: () => void;
  sortCallback?: (records: any[]) => void;
  actionButtons?: ["add" | "reload" | "back"];
  exButtons?: any[];
  addChild?: boolean;
}

interface editorProps {
  type: "number" | "text" | "select" | "date";
  required: boolean;
  idField: string;
  valueField: string;
  textField: string;
  data: any[];
}
interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  editor: editorProps;
  dataIndex: string;
  title: any;
  record: any;
  index: number;
  children: React.ReactNode;
}

const SortableItem = SortableElement((props: any) => <tr {...props} />);
const SortContainer = SortableContainer((props: any) => <tbody {...props} />);
const DragHandle = SortableHandle(() => <MenuOutlined />);

export default class EditableTable extends Component<IProps> {
  state = {
    data: this.props.datas,
    editingId: "",
    editingRecord: {},
    editingSelected: {},
  };
  formRef = React.createRef<FormInstance>();

  EditableCell: React.FC<EditableCellProps> = ({
    editing,
    editor,
    dataIndex,
    title,
    record,
    index,
    children,
    ...restProps
  }) => {
    // 暂存行编辑中的
    const onSelectChange = (val: any, item: any) => {
      const oldState = this.state.editingSelected;
      this.setState({
        editingSelected: {
          ...oldState,
          [editor.idField]: item.value,
          [dataIndex]: item.children,
        },
      });
    };
    let inputNode = <></>;
    if (editor) {
      switch (editor.type) {
        case "number":
          inputNode = <InputNumber />;
          break;
        case "date":
          inputNode = <DatePicker />;
          break;
        case "select":
          inputNode = (
            <Select allowClear={!editor.required} onChange={onSelectChange}>
              {DataUtil.selectOptionsRender(
                editor.data,
                editor.valueField,
                editor.textField,
                "a"
              )}
            </Select>
          );
          break;
        default:
          inputNode = <Input />;
      }
    }

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            key={dataIndex}
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: editor.required }]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  // @ts-ignore
  onSortEnd = ({ oldIndex, newIndex }) => {
    if (this.props.sortCallback) {
      const { data } = this.state;
      if (oldIndex !== newIndex) {
        let newData: any[] = arrayMoveImmutable(
          // @ts-ignore
          [].concat(data),
          oldIndex,
          newIndex
        ).filter((el) => !!el);
        console.log("Sorted items: ", newData);
        for (let i = 0; i < newData.length; i++) {
          newData[i]["sort_index"] = i + 1;
        }
        this.props.sortCallback(newData);
        this.setState({ data: newData });
      }
    }
  };

  DraggableContainer = (props: any) => (
    <SortContainer
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={this.onSortEnd}
      {...props}
    />
  );

  // @ts-ignore
  DraggableBodyRow = ({ className, style, ...restProps }) => {
    const { data } = this.state;
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = data.findIndex(
      (x: any) => x[this.props.record_key] === restProps["data-row-key"]
    );
    //console.log(index,restProps)
    return <SortableItem key={index} index={index} {...restProps} />;
  };

  render() {
    const isEditing = (record: any) =>
      record[this.props.record_key] === this.state.editingId;
    const columns = this.props.columns;

    if (
      this.props.addChild &&
      columns.findIndex((item) => item.dataIndex === "addChild") < 0
    ) {
      columns.push({
        title: "子项",
        key: "addChild",
        dataIndex: "addChild",
        align: "center" as "center",
        width: 88,
        render: (_3: any, record: any) => {
          return (
            <Typography.Link
              disabled={this.state.editingId !== ""}
              onClick={() => AddChild(record[this.props.record_key])}
            >
              <PlusCircleOutlined />
            </Typography.Link>
          );
        },
      });
    }
    if (
      this.props.sortCallback &&
      columns.findIndex((item) => item.key === "sort") < 0
    ) {
      columns.push({
        title: "排序",
        key: "sort",
        dataIndex: "sort",
        align: "center" as "center",
        width: 88,
        className: "drag-visible",
        render: (_2: any, record: any) => {
          return (
            <Typography.Link disabled={this.state.editingId !== ""}>
              <DragHandle />
            </Typography.Link>
          );
        },
      });
    }
    if (columns.findIndex((item) => item.key === "operation") < 0) {
      //let c = this.state.columns;
      columns.push({
        title: "编辑",
        key: "operation",
        dataIndex: "operation",
        width: 128,
        align: "center" as "center",
        render: (_1: any, record: any) => {
          const editable = isEditing(record);
          return editable ? (
            <Space size="small" split={<Divider type="vertical" />}>
              <Typography.Link
                onClick={() => save(record[this.props.record_key])}
              >
                <SaveOutlined />
              </Typography.Link>
              <Typography.Link onClick={cancel}>
                <StopOutlined />
              </Typography.Link>
            </Space>
          ) : (
            <Space size="small" split={<Divider type="vertical" />}>
              <Typography.Link
                disabled={this.state.editingId !== ""}
                onClick={() => edit(record)}
              >
                <FormOutlined />
              </Typography.Link>
              <Popconfirm
                disabled={this.state.editingId !== ""}
                title="确定要删除这条记录吗"
                okText="删除"
                cancelText
                onConfirm={() => Delete(record[this.props.record_key])}
              >
                <Typography.Link disabled={this.state.editingId !== ""}>
                  <DeleteOutlined />
                </Typography.Link>
              </Popconfirm>
            </Space>
          );
        },
      });
      //this.setState({columns: c});
    }

    const Reload = () => {
      cancel();
      this.props.reloadCallback();
    };
    const Add = () => {
      this.formRef.current!.resetFields();
      const newData = [...this.state.data];
      const newRecord = {
        [this.props.record_key]: "0",
        sort_index:
          DataUtil.findMaxMin(newData, "sort_index").max.sort_index + 1,
      };
      newData.push(newRecord);
      this.setState({
        data: newData,
        editingId: "0",
        editingRecord: newRecord,
      });
    };
    const AddChild = (key_id: number) => {
      let parent_id = key_id;
      let newRecord = { [this.props.record_key]: "0", parent_id: parent_id };
      const FindToAdd = (d: any[]) => {
        for (let i = 0; i < d.length; i++) {
          if (d[i][this.props.record_key] === parent_id) {
            if (!d[i].children) {
              d[i].children = [];
            }
            newRecord.sort_index =
              DataUtil.findMaxMin(d[i].children, "sort_index").max.sort_index +
              1;
            d[i].children.push(newRecord);
            break;
          }
          if (d[i].children) {
            d[i].children = FindToAdd(d[i].children);
          }
        }
        return d;
      };
      const newData = FindToAdd([...this.state.data]);
      this.formRef.current!.resetFields();
      this.setState({
        data: newData,
        editingId: "0",
        editingRecord: newRecord,
      });
    };
    const edit = (record: any) => {
      this.formRef.current!.setFieldsValue({ ...record });
      this.setState({
        editingId: record[this.props.record_key],
        editingRecord: record,
      });
    };
    const cancel = () => {
      const newData = [...this.state.data];
      const index = newData.findIndex(
        (item) => item[this.props.record_key] === "0"
      );
      if (index > -1) {
        newData.splice(index, 1);
        this.setState({
          data: newData,
        });
      }
      this.setState({
        editingId: "",
        editingRecord: null,
        changeSelect: {},
      });
    };
    const Delete = (record_key: string) => {
      const newData = myFlat(this.state.data);
      const index = newData.findIndex(
        (item) => record_key === item[this.props.record_key]
      );
      if (index > -1) {
        this.props.deleteCallback(record_key);
        newData.splice(index, 1);
        this.setState({ data: newData, editingId: "", changeSelect: {} });
      }
    };
    const save = async (key: React.Key) => {
      try {
        const row = await this.formRef.current!.validateFields();

        //const newData = [...this.state.data];
        const editingRecord = this.state.editingRecord;
        const editingSelected = this.state.editingSelected;
        const newRecord = { ...editingRecord, ...row, ...editingSelected };
        // const index = newData.findIndex(item => key === item[this.props.record_key]);
        // if (index > -1) {
        //     const item = newData[index];
        //     newRecord = {...item, ...row, ...editingSelected};
        //     if (key === '0' || key === 0 ){
        //         newRecord[this.props.record_key] = null;
        //     }
        //     newData.splice(index, 1, newRecord);
        //     this.setState({data: newData, editingId: '', changeSelect: {}});
        // } else {
        //     newRecord = {...row, ...editingSelected};
        //     newRecord[this.props.record_key] = null;
        //     newData.push(newRecord);
        //     this.setState({data: newData, editingId: '', changeSelect: {}});
        // }
        //console.log('newRecord:', newRecord);
        if (key === "0" || key === 0) {
          newRecord[this.props.record_key] = null;
        }
        this.props.saveCallback(newRecord);
      } catch (errInfo) {
        console.log("Validate Failed:", errInfo);
      }
    };

    const mergedColumns = columns.map((col) => {
      if (!col.editor) {
        return col;
      }

      return {
        ...col,
        onCell: (record: any) => ({
          record,
          editor: col.editor,
          dataIndex: col.dataIndex,
          title: col.dataIndex,
          editing: isEditing(record),
        }),
      };
    });
    const actionButtons = this.props.actionButtons || ["add", "reload"];
    return (
      <div key="ttt1">
        <Space size={16} style={{ marginBottom: 16 }}>
          <Button
            key="K1"
            type="primary"
            disabled={this.state.editingId !== ""}
            onClick={Add}
            hidden={actionButtons.findIndex((i) => i === "add") === -1}
          >
            <PlusCircleOutlined /> 新增
          </Button>
          <Button
            key="K2"
            type="primary"
            onClick={Reload}
            hidden={actionButtons.findIndex((i) => i === "reload") === -1}
          >
            <RedoOutlined /> 刷新
          </Button>
          {this.props.exButtons}
        </Space>
        <Form ref={this.formRef} component={false}>
          <Table
            components={{
              body: {
                wrapper: this.DraggableContainer,
                row: this.DraggableBodyRow,
                cell: this.EditableCell,
              },
            }}
            rowKey={this.props.record_key}
            size="small"
            bordered
            dataSource={this.state.data}
            columns={mergedColumns}
            rowClassName="editable-row"
            pagination={false}
            indentSize={24}
            defaultExpandAllRows={true}
            expandIconColumnIndex={1}
          />
        </Form>
      </div>
    );
  }
}

//export default EditableTable
